import { takeLatest, select, call, put, all } from 'redux-saga/effects';
import get from 'lodash/get';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { createGuestAccount } from 'yoda-interfaces/lib/Account/AccountApi';
import { isFeatureEnabledFromCookie } from 'yoda-core-components/lib/helpers/Utils/Utils';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { initiateCheckout } from 'yoda-interfaces/lib/Order/OrderApi';
import { getCookie } from 'yoda-interfaces/lib/helpers/Cookies';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { DP_USER_STATE, SET_SERVICE_ERROR } from 'yoda-site-components/lib/common/Constants';
import {
    URL_CART_PAGE,
    URL_CHECKOUT_PAYMENT,
    URL_CHECKOUT_PAGE,
    URL_REVIEW_PAGE,
    ORDER_REVIEW_ERROR_SUBCODE,
    ANALYTICS_PAGE_NAME_SINGLE_CLICK_CHECKOUT,
} from 'yoda-checkout-components/lib/common/Constants';
import {
    isDiscontinuedItem,
    getClientId,
    handleAuroraRedirection,
} from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import {
    isOOSItem,
    scrollToElement,
} from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { pageLoadSuccessAnalyticsAction } from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import * as SingleClickCheckoutActionTypes from 'yoda-checkout-components/lib/actionTypes/SingleClickCheckoutActionTypes';
import * as orderSubmittableAction from 'yoda-checkout-components/lib/actions/OrderSubmittableAction';
import { cartDetailsSaga } from './CartDetailsSaga';

function* singleClickCheckoutSessionInit(actionParam) {
    const action = actionParam;
    action.payload = action.payload ? action.payload : {};
    const context = (state) => state.context;
    const cartDetails = (state) => state.cartDetails;
    const orderItems = (state) => state.orderItems;
    const appContext = yield select(context);
    const cartDetailsData = yield select(cartDetails);
    const orderItemsData = yield select(orderItems);
    const exceptions = get(cartDetailsData, 'data.status.exceptions', []);
    const enableDiscontinuedItemsMessaging = get(
        appContext,
        'featureFlags.enableDiscontinuedItemsMessaging',
        true
    );
    const enableGoogleAddressValidation = get(
        appContext,
        'featureFlags.enableGoogleAddressValidation',
        true
    );
    const isSomeOfItemOOS = enableDiscontinuedItemsMessaging
        ? find(exceptions, { errorCode: 'CODE_FOR_DISCONTINUED_AND_OR_OOS_ITEMS' })
        : find(exceptions, { errorCode: 'ORDER_HAS_OUT_OF_STOCK' });
    const { deviceType } = appContext;
    const isNativeIos = get(appContext, 'isNativeIos', false);
    const isNativeAndroid = get(appContext, 'isNativeAndroid', false);
    const enablePreSourcing = get(appContext, 'featureFlags.enablePreSourcing', true);
    const { disableBnpl = false } = get(appContext, 'featureFlags', {});
    // If user state is 2, then create guest account and continue. If guest account creation failed then return.
    yield put(showGlobalLoader(true));
    if (TokenProvider.get(DP_USER_STATE) === '2') {
        const option = {};
        option.headers = {
            'X-DPOrder': getCookie('DPOrder'),
        };
        const isGuestAccountCreated = yield call(createGuestAccount, appContext, option);
        if (!isGuestAccountCreated) {
            yield put(showGlobalLoader(false));
            return;
        }
    }

    if (!isEmpty(deviceType)) {
        action.payload.clientId = getClientId(deviceType, isNativeIos, isNativeAndroid);
    }

    if (enablePreSourcing) {
        action.payload.xClientSource = 'CART';
    }

    action.payload.xCommand = 'initiate-checkout';
    if (isFeatureEnabledFromCookie('enableGAV') || enableGoogleAddressValidation) {
        action.payload.isGoogleAddressVerficationRequired = true;
    }
    action.payload.errorHandler = {
        errorComponent: 'CART_SECTION',
    };
    action.payload.scc = true;
    if (isSomeOfItemOOS) {
        /* For Aurora => sending oosItems ids instead of just sending some of the items are OOS */
        const itemIDs = [];
        forEach(orderItemsData, (item) => {
            if (isOOSItem(item) || isDiscontinuedItem(item)) {
                itemIDs.push(item.id);
            }
        });
        if (itemIDs) {
            action.payload.itemsToBeDeleted = itemIDs;
        }
    }
    const result = yield call(FactorySaga, initiateCheckout, action);
    if (result.isSuccess) {
        let targetURL = URL_CHECKOUT_PAGE;

        const initiateCheckoutExceptions = get(result, 'response.data.status.exceptions', []);

        const responseSubmittable = get(result, 'response.data.status.submittable', false);
        const exceptionsReview = get(result, 'response.data.status.exceptions', []);
        let rewardPromoFinanceConflict = false;
        if (exceptionsReview && exceptionsReview.length === 1) {
            rewardPromoFinanceConflict =
                exceptionsReview[0].errorSubcode &&
                exceptionsReview[0].errorSubcode === ORDER_REVIEW_ERROR_SUBCODE;
        }
        const analyticsAttributes = get(result, 'response.data.analyticsAttributes', []);
        const expressCO = find(analyticsAttributes, { type: 'expressCheckoutEligible' }) || {};
        const paypal = get(result, 'response.data.billing.paypal', {});
        const giftCards = get(result, 'response.data.billing.unnamed.giftCards', []);

        const strategy = result.response.headers.get('X-STRATEGY');
        const isBnplSelected = LocalStorage.getData('bnplSelected') === 'true';
        /**
         * To handle BNPL Payment flow
         * To Check register user have default address & payment added in profile.
         * Express || Expedite redirect to Payment Page to select BNPL Flow
         */
        if (
            !disableBnpl &&
            isBnplSelected &&
            TokenProvider.get(DP_USER_STATE) === '1' &&
            (expressCO.value === 'true' ||
                includes(strategy, 'expedite-checkout') ||
                responseSubmittable)
        ) {
            targetURL = responseSubmittable ? URL_REVIEW_PAGE : URL_CHECKOUT_PAYMENT;
        } else if (
            expressCO.value === 'true' &&
            responseSubmittable &&
            exceptionsReview.length === 0 &&
            isEmpty(paypal) &&
            isEmpty(giftCards)
        ) {
            action.payload.pageName = 'review';
            action.payload.expand = 'status,delivery,billing';
            if (!isEmpty(deviceType)) {
                action.payload.clientId = getClientId(deviceType, isNativeIos, isNativeAndroid);
            }
            const cartDetailAction = {
                payload: {
                    loadSingleClickCheckout: true,
                },
            };
            yield call(cartDetailsSaga, cartDetailAction);
            const getLoadApiFailed = (state) =>
                get(state, 'SingleClickOrderInfo.loadApiFailed', '');
            const isLoadApiFailed = yield select(getLoadApiFailed);
            if (isLoadApiFailed) {
                if (isLoadApiFailed === 'empty-scroll') {
                    scrollToElement();
                }
                return;
            }
            const cartDetailsSelector = (state) => state.cartDetails;
            const orderDetailResponse = yield select(cartDetailsSelector);

            const orderExceptions = get(orderDetailResponse, 'data.status.exceptions', []);
            const orderWarnings = get(orderDetailResponse, 'data.status.warnings', []);
            if (orderDetailResponse && isEmpty(orderExceptions) && isEmpty(orderWarnings)) {
                const { data: { delivery: { groups = [] } = {} } = {} } = orderDetailResponse;
                const storeDetail = groups.find((group) => group.type === 'STORE');
                if (storeDetail && storeDetail.store) {
                    window.location.replace(URL_REVIEW_PAGE);
                } else {
                    yield put(showGlobalLoader(false));
                    yield put({
                        type:
                            SingleClickCheckoutActionTypes.SINGLE_CLICK_CHECKOUT_SESSION_INIT_RESPONSE,
                        orderDetailResponse,
                    });
                    yield put(
                        pageLoadSuccessAnalyticsAction(ANALYTICS_PAGE_NAME_SINGLE_CLICK_CHECKOUT)
                    );
                    yield put(orderSubmittableAction.setOrderSubmittable(true));
                }
            } else {
                window.location.replace(URL_REVIEW_PAGE);
            }
        } else if (
            includes(strategy, 'expedite-checkout') ||
            responseSubmittable ||
            rewardPromoFinanceConflict
        ) {
            if (!rewardPromoFinanceConflict) {
                LocalStorage.setData('fetchPromoOffers', 1, true);
            }
            window.location.replace(URL_REVIEW_PAGE);
        } else {
            /* *
             * Calling handleAuroraRedirection to check if there any any exceptions and redirect
             * */
            const redirectToPayment = handleAuroraRedirection(initiateCheckoutExceptions);
            if (redirectToPayment) {
                targetURL = `${URL_CHECKOUT_PAYMENT}`;
            }
            window.location.replace(targetURL);
        }
    } else if (result.response) {
        yield put(showGlobalLoader(false));
        document && document.body.classList.remove('hidden-overflow');
        const errorHandlerInfo = {};
        const errorInfo = Array.isArray(result.response.data)
            ? result.response.data[0]
            : result.response.data;
        errorHandlerInfo.errorComponent = 'CART_SECTION';
        errorHandlerInfo.errorCode = errorInfo?.errorCode;
        errorHandlerInfo.errorMessage = errorInfo?.errorMessage;
        const { enableRewardRedemption = true } = get(appContext, 'featureFlags', {});
        if (
            enableRewardRedemption &&
            errorHandlerInfo.errorCode !== 'API_ERR_REDEEMED_COUPON_PRESENT'
        ) {
            yield all([
                put({
                    type: SET_SERVICE_ERROR,
                    errorHandlerInfo,
                }),
            ]);
        }
        if (errorInfo?.errorCode === 'SRV_TOTAL_EXCEEDED_ERROR') {
            LocalStorage.setData('TOTAL_EXCEEDED_ERROR', 1, true);
            getBrowserHistory().push(URL_CART_PAGE);
        }
        if (
            errorInfo?.errorCode === 'SRV_ITEMS_NOT_AVAILABLE' ||
            (enableRewardRedemption && errorInfo?.errorCode === 'API_ERR_REDEEMED_COUPON_PRESENT')
        ) {
            window.location.href = URL_CART_PAGE;
        }
    }
}

const watchSingleClickCheckoutSessionInitSaga = function* watchSingleClickCheckoutSessionInitSaga() {
    yield takeLatest(
        SingleClickCheckoutActionTypes.SINGLE_CLICK_CHECKOUT_SESSION_INIT,
        singleClickCheckoutSessionInit
    );
};

watchSingleClickCheckoutSessionInitSaga.sagaName = 'watchSingleClickCheckoutSessionInitSaga';

export default watchSingleClickCheckoutSessionInitSaga;
